import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './LocationEdit.module.css';
import { locationApi, Country, City } from '../../services/locationApi';

const LocationEdit: React.FC = () => {
  const { countryId } = useParams<{ countryId: string }>();
  const navigate = useNavigate();
  const isEdit = !!countryId;

  const [country, setCountry] = useState<Country>({
    Country_ID: '',
    name: '',
    abbrev: ''
  });
  const [cities, setCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [newCityName, setNewCityName] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fetch country and cities on component mount if editing
  useEffect(() => {
    if (isEdit && countryId) {
      fetchCountryData();
      fetchCities();
    }
  }, [isEdit, countryId]);

  // Filter cities when search term changes
  useEffect(() => {
    if (citySearchTerm) {
      const filtered = cities.filter(city =>
        city.name.toLowerCase().includes(citySearchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities(cities);
    }
  }, [citySearchTerm, cities]);

  const fetchCountryData = async () => {
    if (!countryId) {
      return;
    }
    try {
      setLoading(true);
  const data = await locationApi.getCountryById(countryId);
      setCountry(data);
    } catch (error: any) {
      console.error('Error fetching country:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load country data. Please try again later.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    if (!countryId) {
      return;
    }
    try {
      setCitiesLoading(true);
  const data = await locationApi.getAllCities(countryId);
      setCities(data);
      setFilteredCities(data);
    } catch (error: any) {
      console.error('Error fetching cities:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load cities. Please try again later.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setCitiesLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!country.name.trim()) {
      newErrors.name = 'Country name is required';
    }

    if (!country.abbrev.trim()) {
      newErrors.abbrev = 'Country abbreviation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      setLoading(true);
      
      const countryData = {
        name: country.name.trim(),
        abbrev: country.abbrev.trim()
      };

      let result;
      if (isEdit) {
        if (!countryId) {
          throw new Error('Country ID is missing.');
        }
        result = await locationApi.updateCountry(countryId, countryData);
        toast.success('Country updated successfully', {
          position: "top-right",
          autoClose: 3000
        });
      } else {
        result = await locationApi.createCountry(countryData);
        toast.success('Country created successfully', {
          position: "top-right",
          autoClose: 3000
        });
      }

      // Navigate to the edit page if it was a creation
      if (!isEdit && result.Country_ID) {
        navigate(`/admin/locations/${result.Country_ID}`);
      }
    } catch (error: any) {
      console.error('Error saving country:', error);
      const errorMessage = error?.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} country. Please try again.`;
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCity = async () => {
    if (!countryId) {
      toast.error('Missing country context.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }
    if (!newCityName.trim()) {
      toast.error('Please enter a city name.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      const cityData = {
        name: newCityName.trim(),
        Country_ID: countryId
      };

      await locationApi.createCity(cityData);
      setNewCityName('');
      fetchCities(); // Refresh cities list
      toast.success('City created successfully', {
        position: "top-right",
        autoClose: 3000
      });
    } catch (error: any) {
      console.error('Error creating city:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to create city. Please try again.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleDeleteCity = (cityId: string, cityName: string) => {
    toast.dismiss(); // Clear any existing toasts
    
    toast(
      <div>
        <p>Are you sure you want to delete the city "{cityName}"?</p>
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={async () => {
              toast.dismiss();
              try {
                await locationApi.deleteCity(cityId);
                fetchCities(); // Refresh cities list
                toast.success('City deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
              } catch (error: any) {
                console.error('Error deleting city:', error);
                const errorMessage = error?.response?.data?.detail || 'Failed to delete city. Please try again.';
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  if (loading && isEdit) {
    return (
      <div className="container mt-4">
        <div className={styles.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">Loading country data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Toast Container */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/admin/locations">Country Management</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {isEdit ? country.name || 'Edit Country' : 'Create Country'}
          </li>
        </ol>
      </nav>

      <div className="container-xl px-4 mt-4">
        <div className="row g-4">
          {/* Country Details Section */}
          <div className={`col-12 ${isEdit ? 'col-lg-8' : ''}`}>
            <div className={`card ${styles.card} h-100`}>
              <div className={`card-header ${styles.cardHeader}`}>
                <h5 className="mb-0">
                  <i className="bi bi-geo-alt me-2"></i>
                  Country Details
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Country Name */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="inputName">
                        Country Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${styles.formControl} ${errors.name ? styles.isInvalid : ''}`}
                        id="inputName" 
                        type="text"
                        placeholder="Enter Country Name" 
                        value={country.name}
                        onChange={(e) => setCountry(prev => ({ ...prev, name: e.target.value }))}
                        required 
                      />
                      {errors.name && <div className="text-danger small mt-1">{errors.name}</div>}
                    </div>

                    {/* Country Abbreviation */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="inputAbbrev">
                        Abbreviation <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${styles.formControl} ${errors.abbrev ? styles.isInvalid : ''}`}
                        id="inputAbbrev" 
                        type="text" 
                        placeholder="Enter Country Abbreviation"
                        value={country.abbrev}
                        onChange={(e) => setCountry(prev => ({ ...prev, abbrev: e.target.value }))}
                        maxLength={3}
                        style={{ textTransform: 'uppercase' }}
                        required 
                      />
                      {errors.abbrev && <div className="text-danger small mt-1">{errors.abbrev}</div>}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex gap-2 mt-4 pt-3 border-top">
                    <button 
                      className="btn btn-primary px-4" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {isEdit ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          {isEdit ? 'Update Country' : 'Create Country'}
                        </>
                      )}
                    </button>
                    <Link to="/admin/locations" className="btn btn-outline-secondary px-4">
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Countries
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* City Management Section - Only show if editing */}
          {isEdit && (
            <div className="col-12 col-lg-4">
              <div className={`card ${styles.card} h-100`}>
                <div className={`card-header ${styles.cardHeader}`}>
                  <h5 className="mb-0">
                    <i className="bi bi-buildings me-2"></i>
                    City Management
                  </h5>
                </div>
                <div className="card-body">
                  {/* Add New City */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Add New City</label>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className={`form-control ${styles.formControl}`}
                        placeholder="Enter city name" 
                        value={newCityName}
                        onChange={(e) => setNewCityName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateCity();
                          }
                        }}
                      />
                      <button 
                        className="btn btn-success" 
                        type="button"
                        onClick={handleCreateCity}
                        disabled={!newCityName.trim()}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add
                      </button>
                    </div>
                  </div>
                  
                  {/* Existing Cities */}
                  <div>
                    <label className="form-label fw-semibold">
                      Existing Cities 
                      {cities.length > 0 && (
                        <span className="badge bg-secondary ms-2">{cities.length}</span>
                      )}
                    </label>
                    
                    {/* Search Input */}
                    <div className="mb-3">
                      <input 
                        type="text" 
                        className={`form-control ${styles.searchInput}`}
                        placeholder="Search cities..."
                        value={citySearchTerm}
                        onChange={(e) => setCitySearchTerm(e.target.value)}
                      />
                    </div>
                    
                    {/* Cities List */}
                    <div className={styles.scrollView}>
                      {citiesLoading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <div className="mt-2 small text-muted">Loading cities...</div>
                        </div>
                      ) : filteredCities.length === 0 ? (
                        <div className="text-center text-muted p-4">
                          <i className="bi bi-buildings display-6 text-muted mb-2"></i>
                          <div className="small">
                            {cities.length === 0 ? 'No cities added yet' : 'No cities match your search'}
                          </div>
                        </div>
                      ) : (
                        <div className="list-group list-group-flush">
                          {filteredCities.map(city => (
                            <div key={city.City_ID} className={`list-group-item ${styles.cityItem}`}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-medium">{city.name}</span>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteCity(city.City_ID, city.name)}
                                  title={`Delete ${city.name}`}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationEdit;
